import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LifeBuoy, Mail, MessageCircle, HelpCircle, Send, Loader } from 'lucide-react';
import { authFetch } from '../utils/authFetch';

const DEFAULT_TOPICS = [
  'Account & Login',
  'Portfolio & Watchlist',
  'Alerts & Notifications',
  'Billing & Subscription',
  'Bug Report',
  'Feature Request',
  'Other',
];

const DEFAULT_MAX_WORDS = 250;

const SUPPORT_CHANNELS = [
  {
    icon: Mail,
    label: 'Email Support',
    value: 'support@bidsig.com',
    href: 'mailto:support@bidsig.com',
    note: 'We typically respond within one business day.',
  },
  {
    icon: MessageCircle,
    label: 'Live Chat',
    value: 'Available 24/7',
    note: 'In-app chat for account and product questions.',
  },
];

function countWords(text) {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}

const Support = () => {
  const [topics, setTopics] = useState(DEFAULT_TOPICS);
  const [maxWords, setMaxWords] = useState(DEFAULT_MAX_WORDS);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [topicSelection, setTopicSelection] = useState(DEFAULT_TOPICS[0]);
  const [otherTopicText, setOtherTopicText] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  const wordCount = useMemo(() => countWords(message), [message]);
  const isOtherTopic = topicSelection === 'Other';
  const overWordLimit = wordCount > maxWords;

  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      setUser(storedUser);
      if (storedUser?.email) setEmail(storedUser.email);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    fetch('/api/support/topics')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data?.topics) && data.topics.length > 0) {
          setTopics(data.topics);
          setTopicSelection((current) => (data.topics.includes(current) ? current : data.topics[0]));
        }
        if (typeof data?.maxMessageWords === 'number') {
          setMaxWords(data.maxMessageWords);
        }
      })
      .catch(() => { /* use defaults */ });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (overWordLimit) {
      setFormError(`Message must be at most ${maxWords} words.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch('/api/support/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: topicSelection,
          customTopic: isOtherTopic ? otherTopicText.trim() : undefined,
          message: message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send message');

      setFormSuccess(data.message || 'Your message has been sent.');
      setMessage('');
      setOtherTopicText('');
      setTopicSelection(topics[0]);
    } catch (err) {
      setFormError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content info-page support-page">
      <header className="info-page-header info-page-header--actions">
        <h2>
          <LifeBuoy size={22} className="info-page-header-icon" aria-hidden />
          Support
        </h2>
        <div className="info-page-header-actions">
          <Link to="/help" className="pw-btn pw-btn-primary">
            <HelpCircle size={16} aria-hidden />
            Help Center
          </Link>
        </div>
      </header>

      <section className="info-section" aria-labelledby="support-channels-title">
        <h2 id="support-channels-title" className="info-section-title">Get Support</h2>
        <p className="info-section-desc">
          Start with our <Link to="/help">Help Center</Link> for guides on watchlists, portfolios, signals, and alerts. If you still need assistance, reach out through any channel below.
        </p>
        <div className="info-contact-grid">
          {SUPPORT_CHANNELS.map(({ icon: Icon, label, value, href, note }) => (
            <article key={label} className="info-card info-contact-card">
              <div className="info-card-icon info-card-icon--support" aria-hidden>
                <Icon size={22} />
              </div>
              <h3 className="info-card-title">{label}</h3>
              {href ? (
                <a href={href} className="info-contact-value">{value}</a>
              ) : (
                <p className="info-contact-value">{value}</p>
              )}
              {note && <p className="info-card-text">{note}</p>}
            </article>
          ))}
        </div>
      </section>

      <section className="info-section" aria-labelledby="contact-us-title">
        <h2 id="contact-us-title" className="info-section-title">Contact Us</h2>

        {!user ? (
          <div className="support-contact-signin info-data-card">
            <Mail size={40} aria-hidden />
            <h3>Sign in to contact support</h3>
            <p>
              Email support is available to signed-in members. Please{' '}
              <Link to="/login">sign in</Link> to send a message to{' '}
              <strong>support@bidsig.com</strong>.
            </p>
            <Link to="/login" className="pw-btn pw-btn-primary">Sign in</Link>
          </div>
        ) : (
          <>
            <p className="info-section-desc">
              Send a message to <strong>support@bidsig.com</strong>. We&apos;ll reply to your account email.
            </p>

            <form className="support-contact-form info-data-card" onSubmit={handleSubmit} noValidate>
          {formSuccess && (
            <div className="support-form-banner support-form-banner--success" role="status">
              {formSuccess}
            </div>
          )}
          {formError && (
            <div className="support-form-banner support-form-banner--error" role="alert">
              {formError}
            </div>
          )}

          <div className="support-contact-form-grid">
            <label className="support-contact-field">
              <span>Your email</span>
              <input
                type="email"
                value={email}
                readOnly
                className="support-contact-email-readonly"
                aria-readonly="true"
              />
            </label>

            <div className="support-contact-field">
              <span>Topic</span>
              {isOtherTopic ? (
                <div className="support-topic-control">
                  <input
                    type="text"
                    value={otherTopicText}
                    onChange={(e) => setOtherTopicText(e.target.value)}
                    placeholder="Enter your topic"
                    maxLength={80}
                    required
                    aria-label="Custom topic"
                  />
                  <button
                    type="button"
                    className="support-topic-switch"
                    onClick={() => {
                      setTopicSelection(topics[0]);
                      setOtherTopicText('');
                    }}
                  >
                    Choose preset topic
                  </button>
                </div>
              ) : (
                <select
                  value={topicSelection}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTopicSelection(value);
                    if (value !== 'Other') setOtherTopicText('');
                  }}
                  required
                  aria-label="Topic"
                >
                  {topics.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <label className="support-contact-field support-contact-field--full">
            <span>Message</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your question or issue in detail..."
              rows={6}
              required
              aria-describedby="support-word-count"
            />
            <div
              id="support-word-count"
              className={`support-word-count ${overWordLimit ? 'support-word-count--over' : ''}`}
              aria-live="polite"
            >
              {wordCount} / {maxWords} words
            </div>
          </label>

          <div className="support-contact-form-actions">
            <button
              type="submit"
              className="pw-btn pw-btn-primary support-contact-submit"
              disabled={submitting || overWordLimit}
            >
              {submitting ? <Loader size={16} className="spinning" aria-hidden /> : <Send size={16} aria-hidden />}
              {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </div>
        </form>
          </>
        )}
      </section>
    </div>
  );
};

export default Support;
